import { FC, useCallback, useEffect } from 'react';
import { DrawerProps } from './types';
import clsx from 'clsx';
import Card, { CardBody, CardFooter, CardHeader } from '../Card';
import IconClose from '../../icons/IconClose';
import IconButton from '../IconButton';
import { Transition } from '@headlessui/react';

/**
 *  UI Component
 */
const Drawer: FC<DrawerProps> = (props) => {
  // Apply Default props values
  props = {
    ...{
      size: 'normal',
      position: 'right'
    },
    ...props
  };

  const {
    open,
    onClose,
    position,
    children,
    title,
    headerIcon,
    actions,
    hideHeader,
    notDismissible,
    cardProps,
    cardHeaderProps,
    cardBodyProps,
    cardFooterProps,
    iconCloseClass
  } = props;

  const handleClose = () => {
    if (!notDismissible && onClose) onClose();
  };

  // This is to lock the screen while the drawer is open
  useEffect(() => {
    if (open) {
      document.querySelector('body')?.classList.add('has-open-drawer');
    } else {
      document.querySelector('body')?.classList.remove('has-open-drawer');
    }
    return () =>
      document.querySelector('body')?.classList.remove('has-open-drawer');
  }, [open]);

  // Escape key handling
  const handleEscKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !notDismissible && onClose && open) {
        onClose();
      }
    },
    [notDismissible, onClose, open]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscKey, false);

    return () => {
      document.removeEventListener('keydown', handleEscKey, false);
    };
  }, [handleEscKey]);

  // Card component props
  const cls = styleDrawer(props);

  const elCardProps = {
    seperated: false,
    rounded: false,
    ...cardProps
  };
  const elCardFooterProps = {
    dim: true,
    ...cardFooterProps
  };
  return (
    <Transition show={open} className={clsx(cls.main)}>
      <Transition.Child
        enter="transition duration-1000 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        className={clsx(cls.backdrop)}
        onClick={handleClose}
      ></Transition.Child>
      <Transition.Child
        as={Card}
        enter="transition duration-300 ease-out"
        leave="transition duration-150"
        {...(position === 'left'
          ? {
              enterFrom: 'opacity-0 -translate-x-full',
              enterTo: 'opacity-100 translate-x-0',
              leaveFrom: 'opacity-100 translate-x-0',
              leaveTo: 'opacity-0 -translate-x-full'
            }
          : {
              enterFrom: 'opacity-0 translate-x-full',
              enterTo: 'opacity-100 translate-x-0',
              leaveFrom: 'opacity-100 translate-x-0',
              leaveTo: 'opacity-0 translate-x-full'
            })}
        className={clsx(cls.card)}
        {...elCardProps}
      >
        {!hideHeader && (
          <CardHeader
            icon={headerIcon}
            heading={title}
            actions={
              !notDismissible && (
                <IconButton
                  variant="outlined"
                  size="small"
                  onClick={handleClose}
                >
                  <IconClose className={`${iconCloseClass ?? ''}`} />
                </IconButton>
              )
            }
            {...cardHeaderProps}
          />
        )}
        <CardBody {...cardBodyProps}>{children}</CardBody>
        {actions && <CardFooter {...elCardFooterProps}>{actions}</CardFooter>}
      </Transition.Child>
    </Transition>
  );
};

export default Drawer;

function styleDrawer(props: DrawerProps) {
  const mc = 'Drawer';
  const mcBackdrop = `${mc}__Backdrop`;
  const mcCard = `${mc}__Card`;
  const cls = {
    main: [mc],
    backdrop: [mcBackdrop],
    card: [mcCard]
  };
  if (!props.open) {
    cls.main.push(`${mc}--hidden`);
  }
  if (props.size) {
    cls.main.push(`${mc}--${props.size}`);
  }
  if (props.position) {
    cls.main.push(`${mc}--${props.position}`);
  }
  return cls;
}
